import { Breakpoint, Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppProvider } from '@mej/ui-next';
import { defaultSettings } from '@mej/ui-next/src/components/settings/config-settings';
import type { SettingsState } from '@mej/ui-next/src/components/settings/types';
import { Content, Main } from '@mej/ui-next/src/layouts/auth-split/main';
import { LayoutSection } from '@mej/ui-next/src/layouts/core/layout-section';
import React from 'react';
import ReactDOM from 'react-dom/client';
import 'vite/modulepreload-polyfill';
import { z as zod } from 'zod';

export type SignInSchemaType = zod.infer<typeof SignInSchema>;

export const SignInSchema = zod.object({
    email: zod
        .string()
        .min(1, { message: 'Email is required!' })
        .email({ message: 'Email must be a valid email address!' }),
    password: zod
        .string()
        .min(1, { message: 'Password is required!' })
        .min(6, { message: 'Password must be at least 6 characters!' }),
});

/**
 *
 */
export function Login() {
    const searchParams = new URLSearchParams(window.location.search);
    const isDebug = searchParams.has('debug');

    const renderHead = (
        <Stack spacing={1.5} sx={{}}>
            <Typography variant="h3">Vossloh DLP</Typography>

            <Stack direction="row" spacing={0.5}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    Vossloh is the Nordic region&apos;s leading supplier and one of the world&apos;s
                    leading companies with comprehensive expertise in everything related to modern
                    track technology.
                    <br />
                    <br />
                    To access this application, please sign in with your organization&apos;s
                    account.
                </Typography>
            </Stack>
        </Stack>
    );

    return (
        <>
            {renderHead}

            <Button
                fullWidth
                color="primary"
                variant="contained"
                sx={{ my: 2 }}
                href="/accounts/okta/login/"
            >
                Sign In Via Okta
            </Button>
            {isDebug && (
                <Button
                    fullWidth
                    color="error"
                    variant="outlined"
                    sx={{ my: 2 }}
                    href="/accounts/github/login/"
                >
                    Sign In Via GitHub
                </Button>
            )}

            <Stack direction="row" spacing={0.5}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {`Don't have an account?`}
                    <br />
                    Accounts are managed by your organization.
                </Typography>
            </Stack>
        </>
    );
}

/**
 *
 */
function App() {
    const layoutQuery: Breakpoint = 'md';

    return (
        <LayoutSection
            headerSection={null}
            footerSection={null}
            cssVars={{
                '--layout-auth-content-width': '420px',
            }}
        >
            <Main layoutQuery={layoutQuery}>
                <Content layoutQuery={layoutQuery}>
                    <Login />
                </Content>
            </Main>
        </LayoutSection>
    );
}

export const settings: SettingsState = {
    ...defaultSettings,
    colorScheme: 'light',
} as const;

/** */
const main = () => {
    const container = document.getElementById('app');
    if (!container) throw new Error('App container not found');
    const root = ReactDOM.createRoot(container);
    root.render(
        <React.StrictMode>
            <AppProvider>
                <App />
            </AppProvider>
        </React.StrictMode>,
    );
};

main();
